/**
 * medStorage — encrypted browser-local storage for medication profiles.
 *
 * Data model
 *   profiles: [
 *     {
 *       id: uuid,
 *       name: string (may be initials),
 *       created_at: ISO,
 *       updated_at: ISO,
 *       meds: Medication[],
 *     }
 *   ]
 *
 * Everything lives in the browser via secureStorage. Nothing is sent to
 * a server. Export/import lets the user carry the card to another device.
 */
import { secureGet, secureSet, secureGetJSON, secureRemove } from "./secureStorage";

const PROFILES_KEY = "centennial_med_profiles_v1";
const UNLOCK_KEY = "centennial_med_unlock_v1";
const LARGE_TEXT_KEY = "centennial_med_large_text_v1";

export const FREE_MED_LIMIT = 4;
export const FREE_PROFILE_LIMIT = 1;
export const UNLIMITED_PROFILES = {
  med_safety_unlock_single: 1,
  med_safety_unlock_family: 2,
};

const uuid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36));

const nowIso = () => new Date().toISOString();

export const loadProfiles = () => secureGetJSON(PROFILES_KEY, []);

export const saveProfiles = (profiles) => secureSet(PROFILES_KEY, profiles);

export const createProfile = (name) => {
  const profiles = loadProfiles();
  const p = {
    id: uuid(),
    name: (name || "Untitled").slice(0, 60),
    created_at: nowIso(),
    updated_at: nowIso(),
    meds: [],
  };
  saveProfiles([...profiles, p]);
  return p;
};

export const updateProfile = (id, patch) => {
  const profiles = loadProfiles();
  const next = profiles.map((p) =>
    p.id === id ? { ...p, ...patch, updated_at: nowIso() } : p,
  );
  saveProfiles(next);
  return next.find((p) => p.id === id);
};

export const deleteProfile = (id) => {
  const profiles = loadProfiles().filter((p) => p.id !== id);
  saveProfiles(profiles);
  return profiles;
};

export const addMedication = (profileId, med) => {
  const profiles = loadProfiles();
  const next = profiles.map((p) => {
    if (p.id !== profileId) return p;
    const m = { id: uuid(), created_at: nowIso(), ...med };
    return { ...p, meds: [...p.meds, m], updated_at: nowIso() };
  });
  saveProfiles(next);
  return next.find((p) => p.id === profileId);
};

export const updateMedication = (profileId, medId, patch) => {
  const profiles = loadProfiles();
  const next = profiles.map((p) => {
    if (p.id !== profileId) return p;
    return {
      ...p,
      updated_at: nowIso(),
      meds: p.meds.map((m) => (m.id === medId ? { ...m, ...patch } : m)),
    };
  });
  saveProfiles(next);
  return next.find((p) => p.id === profileId);
};

export const deleteMedication = (profileId, medId) => {
  const profiles = loadProfiles();
  const next = profiles.map((p) => {
    if (p.id !== profileId) return p;
    return { ...p, meds: p.meds.filter((m) => m.id !== medId), updated_at: nowIso() };
  });
  saveProfiles(next);
  return next.find((p) => p.id === profileId);
};

// ---- Export / delete all ----
export const exportAll = () => ({
  exported_at: nowIso(),
  profiles: loadProfiles(),
});

export const importAll = (payload) => {
  if (!payload || !Array.isArray(payload.profiles)) return false;
  saveProfiles(payload.profiles);
  return true;
};

export const deleteAll = () => {
  secureRemove(PROFILES_KEY);
};

// ---- Unlock (paid tier) ----
export const getUnlock = () => secureGet(UNLOCK_KEY) || "";
export const setUnlock = (token) => secureSet(UNLOCK_KEY, token || "");
export const clearUnlock = () => secureRemove(UNLOCK_KEY);

export const isUnlocked = (packageIdOrNull) => {
  const t = getUnlock();
  return !!t && (!packageIdOrNull || t.length > 20);
};

// ---- Large-text preference ----
export const getLargeText = () => secureGet(LARGE_TEXT_KEY) === "1";
export const setLargeText = (on) => secureSet(LARGE_TEXT_KEY, on ? "1" : "0");
